import { get, isEmpty } from 'lodash-es';
import { format } from 'date-fns';

import {
  getTrafficSource,
  isCateringOrderFlow,
  isDineInOrderFlow,
  isMarketplacePlatform,
  isYelpPlatform,
} from '@local/utils';
import {
  ApiRestaurant,
  DeliveryFulfillmentType,
  DineInFulfillmentType,
  FulfillmentType,
  OrderAheadDay,
} from '@local/api';
import { geocode } from '@local/google-maps';

import { deserializeAddress } from 'helpers/address';
import { RESTAURANT_API_VERSION, getFromChownowApi, paths } from 'helpers/api';
import { getBitsTimeSinceMidnight, getDateForApiDateStr } from 'helpers/date';
import { FULFILLMENT_METHODS } from 'helpers/order';

import { UserDataType } from 'types/customer';
import { GoogleMapsType } from 'types/gMaps';
import { FulfillMethod } from 'types/order';
import {
  isClosed,
  isFulfillmentMethodAvailableNow,
} from '@local/data-utils/restaurant';
import { useSelectedAddressStore } from 'store';

export function getChannelParam(restaurantId: string) {
  const cnChannel = isYelpPlatform() ? 'yelp' : getTrafficSource(restaurantId);
  return cnChannel ? `?cn_channel=${cnChannel}` : '';
}

export function getRestaurantEndpoint(restaurantId: string) {
  let path;
  if (isYelpPlatform()) {
    path = paths.yelp.restaurant({ id: restaurantId });
  } else if (isCateringOrderFlow()) {
    path = paths.restaurantCatering({ id: restaurantId });
  } else {
    path = paths.restaurant({ id: restaurantId });
  }
  return `${path}${getChannelParam(restaurantId)}`;
}

export function getFulfillmentForMethod(
  restaurant: Pick<ApiRestaurant, 'fulfillment'>,
  fulfillmentMethod: 'delivery'
): DeliveryFulfillmentType;
export function getFulfillmentForMethod(
  restaurant: Pick<ApiRestaurant, 'fulfillment'>,
  fulfillmentMethod: 'dine_in'
): DineInFulfillmentType;
export function getFulfillmentForMethod(
  restaurant: Pick<ApiRestaurant, 'fulfillment'>,
  fulfillmentMethod: string
): FulfillmentType;

export function getFulfillmentForMethod(
  restaurant: Pick<ApiRestaurant, 'fulfillment'> | undefined,
  fulfillmentMethod: string
) {
  return restaurant?.fulfillment?.[
    fulfillmentMethod as keyof typeof restaurant.fulfillment
  ];
}

export function getIsPickupOnly(restaurant: ApiRestaurant) {
  // If fulfillment.delivery object is empty, then is pickup only
  return isEmpty(
    getFulfillmentForMethod(restaurant, FULFILLMENT_METHODS.delivery)
  );
}

function getFulfillMethod(
  restaurant: ApiRestaurant,
  orderFulfillMethod?: string
): FulfillMethod {
  const hasDineInUrl = isDineInOrderFlow();

  if (orderFulfillMethod) {
    // $TSFIXME
    return orderFulfillMethod as FulfillMethod;
  }
  if (hasDineInUrl) {
    return 'dine_in';
  }
  if (restaurant.delivery_only) {
    return 'delivery'; // Should default to pickup unless HQ is delivery only.
  }
  return 'pickup';
}

export function getFulfillmentMethodIndexRanges(
  day: OrderAheadDay,
  fulfillmentMethod: string
) {
  switch (fulfillmentMethod) {
    case 'curbside':
      return day.curbside_index_ranges;
    case 'delivery':
      return day.delivery_index_ranges;
    case 'pickup':
      return day.pickup_index_ranges;
    default:
      return undefined;
  }
}

export function getFirstAvailableOrderTime(
  restaurant: ApiRestaurant,
  fulfillMethod: string
) {
  const days = restaurant.order_ahead?.days || [];
  let firstAvailableBit = null;
  let date = null;

  for (let i = 0; i < days.length; i += 1) {
    const time = getFulfillmentMethodIndexRanges(days[i], fulfillMethod)?.[0];
    if (time) {
      firstAvailableBit = time.from;
      date = days[i].date;
      break;
    }
  }

  // firstAvailableBit will equal 0 when midnight is first available time
  if (date && (firstAvailableBit || firstAvailableBit === 0)) {
    return {
      firstAvailableBit,
      date,
    };
  }
  return null;
}

export function formatOrderAheadDate({
  startDate,
  firstAvailableBit,
  precision,
}: {
  startDate: string;
  firstAvailableBit: number;
  precision: number;
}) {
  const cleanDate = startDate.replace(/-/g, '');
  const { hours, minutes } = getBitsTimeSinceMidnight({
    firstAvailableBit,
    precision,
  });

  return `${cleanDate}${hours}${minutes}`;
}

export function getNextAvailableTime(
  restaurant: ApiRestaurant,
  orderFulfillMethod?: string
) {
  const orderAhead = restaurant.order_ahead;

  if (isClosed(restaurant)) return {};

  let nextAvailableTime;

  const fulfillMethod = getFulfillMethod(restaurant, orderFulfillMethod);
  const ahead = `is_${fulfillMethod}_ahead_available`;

  if (
    isDineInOrderFlow() ||
    isFulfillmentMethodAvailableNow(fulfillMethod, restaurant)
  ) {
    nextAvailableTime = null;
  } else if (orderAhead && orderAhead[ahead as keyof typeof orderAhead]) {
    const { precision } = orderAhead;
    const firstAvailableOrderTime = getFirstAvailableOrderTime(
      restaurant,
      fulfillMethod
    );

    if (firstAvailableOrderTime) {
      nextAvailableTime = formatOrderAheadDate({
        startDate: firstAvailableOrderTime.date,
        firstAvailableBit: firstAvailableOrderTime.firstAvailableBit,
        precision,
      });
    }
  }

  return {
    fulfillMethod,
    nextAvailableTime,
  };
}

export function getRestaurantHasDelivery(
  restaurant: ApiRestaurant | undefined
) {
  if (!restaurant) return false;

  const delivery = restaurant.fulfillment?.delivery;
  const deliveryOrderAhead =
    restaurant.order_ahead?.is_delivery_ahead_available;

  const deliveryIsUnavailable =
    !delivery || (!delivery.is_available_now && !deliveryOrderAhead);

  if (deliveryIsUnavailable) return false;

  return !!(delivery.is_available_now || delivery.next_available_time);
}

export function getFulfillmentFromUrlQuery(hasDelivery: boolean) {
  const deliversToMe = new URLSearchParams(window.location.search).get(
    'deliversToMe'
  );

  if (deliversToMe === '1' && hasDelivery) {
    return FULFILLMENT_METHODS.delivery;
  }

  return FULFILLMENT_METHODS.pickup;
}

async function geocodeAddressFromURL(locationFromUrl: string) {
  // If there is a # character, we remove it so the geocode api does not fail
  const validLocation = locationFromUrl.replace(/#/g, '');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).google?.maps) {
    try {
      const geocodeResult = await geocode({ address: validLocation });
      const geocodeAddress = deserializeAddress(
        geocodeResult[0] as GoogleMapsType
      );

      // Set the address if it is invalid or not
      if (geocodeAddress) {
        return geocodeAddress;
      }
    } catch {
      return null;
    }
  }

  return null;
}

async function populateDeliveryAddressFromUrl(
  isDeliveryOrder: boolean,
  user: UserDataType
) {
  const urlLocation = new URLSearchParams(window.location.search).get('loc');
  const savedAddressId = new URLSearchParams(window.location.search).get(
    'savedAddressId'
  );
  // An address is in the url, but it is not a referenced to saved address from the user's account
  const shouldUseUrlAddress = isDeliveryOrder && urlLocation;
  const existingCustomerDeliveryAddresses = user?.delivery?.addresses || [];
  const savedAddressObject = existingCustomerDeliveryAddresses.find(
    (address) => address.id === savedAddressId
  );
  // there is an address param in the url and we've found a saved address matching
  const shouldUseSavedAddressFromUrl =
    isDeliveryOrder && urlLocation && !!savedAddressObject;

  if (shouldUseSavedAddressFromUrl) {
    useSelectedAddressStore.getState().setAddress({ ...savedAddressObject });
  } else if (shouldUseUrlAddress) {
    // This causes a slight delay in rendering the menu
    const geocodedAddress = await geocodeAddressFromURL(urlLocation);
    // @ts-expect-error todo look into this typescript error related to Navbar typescript error
    useSelectedAddressStore.getState().setAddress({ ...geocodedAddress });
  }
}

export function handleDeliveryOrderFromURLParam(
  restaurant: ApiRestaurant,
  user: UserDataType
) {
  const hasDelivery = getRestaurantHasDelivery(restaurant);
  const urlQueryFulfillMethod = getFulfillmentFromUrlQuery(hasDelivery) || '';
  const isDeliveryOrder =
    urlQueryFulfillMethod === FULFILLMENT_METHODS.delivery;

  if (!hasDelivery && isDeliveryOrder) {
    return;
  }

  const isMarketplace = isMarketplacePlatform();
  if (isMarketplace) {
    void populateDeliveryAddressFromUrl(isDeliveryOrder, user);
  }
}

export function getIsTableRequired(restaurant: ApiRestaurant) {
  const dineIn = getFulfillmentForMethod(
    restaurant,
    FULFILLMENT_METHODS.dineIn
    // TSFIXME: remove cast when order.js is converted to TypeScript
  ) as DineInFulfillmentType | undefined;

  return !!dineIn?.has_table_names;
}

export function getIsTippingEnabled(
  restaurant: Pick<ApiRestaurant, 'fulfillment'> | undefined,
  fulfillmentMethod: string,
  allowsFlexTipping: boolean | undefined | null
): boolean {
  if (!restaurant || isEmpty(restaurant)) {
    return false;
  }

  const fulfillmentData = getFulfillmentForMethod(
    restaurant,
    fulfillmentMethod
  );
  const allowsTipForFulfillment = !!fulfillmentData?.tip?.allows_tip;

  // if flex tipping isn't explicitly boolean, defer to fulfillment object
  // flex tipping should only be honored for delivery orders
  return typeof allowsFlexTipping === 'boolean' &&
    fulfillmentMethod === FULFILLMENT_METHODS.delivery
    ? allowsFlexTipping
    : allowsTipForFulfillment;
}

export function getIsFulfillmentAvailableForWhen(
  restaurant: ApiRestaurant,
  fulfillMethod: FulfillMethod,
  when: string
) {
  if (!when) {
    return isFulfillmentMethodAvailableNow(fulfillMethod, restaurant);
  }

  const orderAhead = restaurant.order_ahead;

  const whenHour = parseInt(when.substring(8, 10), 10);
  const whenMin = parseInt(when.substring(10, 12), 10);
  const whenIndex = whenHour * 4 + (whenMin / 60) * 4;

  const formattedWhen = getDateForApiDateStr(when);
  const formattedDate = format(formattedWhen, 'yyyy-MM-dd');

  const whenDay = orderAhead?.days.find(({ date }) => date === formattedDate);

  if (!whenDay) {
    return false;
  }

  const fulfillmentIndexesForWhen = getFulfillmentMethodIndexRanges(
    whenDay,
    fulfillMethod
  );

  if (!fulfillmentIndexesForWhen?.length) {
    return false;
  } else {
    let fulfillIsAvailable = true;
    fulfillmentIndexesForWhen.forEach((range) => {
      const fulfillmentIsAvailable =
        whenIndex >= range.from && whenIndex <= range.to;
      if (!fulfillmentIsAvailable) fulfillIsAvailable = false;
    });
    return fulfillIsAvailable;
  }
}

// We need to check both that the membership is enabled AND that the restaurant has plans
export function getRestaurantMembershipEnabled(restaurant: ApiRestaurant) {
  const isEnabled: boolean = get(restaurant, 'membership.is_enabled', false);
  const hasPlans = !!get(restaurant, 'membership.plans', []).length;

  return isEnabled && hasPlans;
}

export function getRestaurant(restaurantId: string) {
  return getFromChownowApi<ApiRestaurant>({
    endpoint: getRestaurantEndpoint(restaurantId),
    apiVersion: RESTAURANT_API_VERSION,
  });
}
